<script lang="ts">
  import * as yup from "yup";
  import { Form } from "$ui/form";
  import { Input } from "$ui/input";

  import { Textarea } from "$ui/textarea";
  import { Button } from "$ui/button";

  import BinIcon from "$assets/icons/bin.svelte";
  import { makeApiRequest } from "$lib/utils/api";
  import { cn } from "$utils/ui";
  import { selectedTeamStore, type TeamApp } from "$stores/teams";

  export let app: TeamApp | undefined = undefined;
  export let onComplete: () => void;

  $: team = $selectedTeamStore;
  $: isNewApp = app === undefined;

  let initialValues = {
    appId: app?.id ?? "",
    description: app?.description ?? "",
    redirectUris: app?.redirectUris ?? [""],
  };

  let validationSchema: any;

  const appIdSchema = yup
    .string()
    .trim("App ID cannot include leading and trailing spaces")
    .strict(true)
    .required("App ID is required");

  const uriShape = yup
    .string()
    .trim("Redirect URI cannot include leading and trailing spaces")
    .strict(true)
    .required("Redirect URI is required")
    .test("is-localhost", "Must be a valid URL", (value) => {
      if (!value) return false;
      const url = new URL(value);
      const isLocalhost = url.hostname == "localhost" && url.protocol.startsWith("http");
      return yup.string().url().isValidSync(value) || isLocalhost;
    });

  if (!isNewApp) {
    validationSchema = yup.object().shape({
      appId: appIdSchema,
      redirectUris: yup.array().of(uriShape).min(1, "At least one Redirect URI is required"),
    });
  } else {
    validationSchema = yup.object().shape({
      appId: appIdSchema.test("unique-app-id", "App ID must be unique", function (value) {
        return !team?.apps.map((app) => app.id.toLowerCase()).includes(value.toLowerCase());
      }),
      redirectUris: yup.array().of(uriShape).min(1, "At least one Redirect URI is required"),
    });
  }

  const onSubmit = async (values: any) => {
    await makeApiRequest(`/teams/${team?.id}/apps/${values.appId}/`, {
      description: values.description,
      redirect_uris: values.redirectUris,
    });
  };

  let form: any = null;
  let errors: any = null;
  let touched: any = null;
  let isSubmitting: any = false;
  let handleSubmit = () => {};

  let validateField: any;
  let updateValidateField: any;

  const add = () => {
    $form.redirectUris = $form.redirectUris.concat("");
    $errors.redirectUris = $errors.redirectUris.concat("");
  };

  const remove = (index: string | number) => () => {
    $form.redirectUris = $form.redirectUris.filter((_: any, currentIndex: string | number) => currentIndex !== index);
    $errors.redirectUris = $errors.redirectUris.filter(
      (_: any, currentIndex: string | number) => currentIndex !== index,
    );
  };
</script>

<div class="flex-1 flex flex-col items-stretch">
  <Form
    {initialValues}
    {validationSchema}
    {onSubmit}
    onSuccess={onComplete}
    bind:form
    bind:handleSubmit
    bind:validateField
    bind:updateValidateField
    bind:isSubmitting
    bind:errors
    bind:touched
  >
    {#if $form}
      <Input
        name="appId"
        label="App ID"
        placeholder="App ID"
        readOnly={!isNewApp}
        autocomplete="off"
        error={$errors.appId}
        bind:value={$form.appId}
        on:input={() => validateField("appId")}
      />

      <Textarea
        name="description"
        label="Description"
        placeholder="Description"
        type="textarea"
        error={$errors.description}
        bind:value={$form.description}
      />

      <div>
        {#each $form.redirectUris as uri, index}
          <div class="flex flex-col gap-4">
            <div class="flex gap-4 [&>div]:flex-1">
              <Input
                name={`redirectUris[${index}]`}
                label="Redirect URL"
                placeholder="Redirect URL"
                bind:value={uri}
                error={$errors?.redirectUris?.[index]}
                on:input={() => {
                  validateField(`redirectUris.${index}`);
                }}
              />

              <div class="!flex-[0_0_24px] mt-9">
                <button
                  type="button"
                  on:click={remove(index)}
                  disabled={$form.redirectUris.length === 1}
                  class={cn("[&>svg]:w-3.5", $form.redirectUris.length === 1 && "opacity-30 cursor-not-allowed")}
                >
                  <BinIcon />
                </button>
              </div>
            </div>
            <div>
              {#if index === $form.redirectUris.length - 1}
                <Button on:click={add} variant="outline" size="xs">+ Add More</Button>
              {/if}
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </Form>
</div>
